import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoIcon from "../../../assets/svg/logoIcon";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import { useCases } from "../../../utils/constants";
import { UseCase } from "../../../utils/interfaces";
import PricingModal from "../modal/pricingModal";
import { ContactModal } from "../modal";

const ChatHeader = ({
	useCase,
	selectUseCase,
}: {
	useCase: number;
	selectUseCase: (selectedCase: number) => void;
}) => {
	const [accountType, setAccountType] = useState<number>(0);
	const [dayGreet, setDayGreet] = useState<string>("");
	const [userLogo, setUserLogo] = useState<string>("");
	const [showPricingModal, setShowPricingModal] = useState<boolean>(false);
	const [showContactModal, setShowContactModal] = useState<boolean>(false);
	const { logout, login} = useAuth();
	const navigate = useNavigate();
	const handleLogout = () => {
		logout();
		navigate("/");
	};

	const getUserLogo = async () => {
		try {
			const axiosInstance = createAxiosInstance({
				"Content-Type": "multipart/form-data",
			});
			const response = await axiosInstance.get(`/logo/${userData?.id}?t=${Date.now()}`, {
				responseType: "arraybuffer",
			});
			
			if(response.status == 401 || response.status === 403) {
				logout();
				navigate("/");
				return;
			}

			// Check if the response contains an error message (JSON format)
			try {
				// Try to decode the response as text to check for JSON error
				const textDecoder = new TextDecoder();
				const responseText = textDecoder.decode(response.data);
				
				// Check if it's a JSON error response
				if (responseText.startsWith('{') && responseText.includes('error')) {
					console.log('Logo not found:', responseText);
					setUserLogo(""); // Set empty logo for error cases
					return;
				}
			} catch (decodeError) {
				// If decoding fails, it's likely binary data, continue with normal processing
			}

			// Process as image data
			const contentType = response.headers["content-type"];
			const base64 = btoa(
				new Uint8Array(response.data).reduce(
					(data, byte) => data + String.fromCharCode(byte),
					""
				)
			);

			if (response.data?.byteLength > 40){
				setUserLogo(`data:${contentType};base64,${base64}`);
			} else {
				setUserLogo("");
			}
		} catch (error: any) {
			console.log(error, "error");
			if(error?.status == 401 || error?.status === 403) {
				logout();
				navigate("/");
			} else {
				// For other errors, set empty logo
				setUserLogo("");
			}
		}
	};

	useEffect(() => {
		const storedUserData = localStorage.getItem("userData");
		const userData = JSON.parse(storedUserData || "{}");

		if (userData?.account_type) {
			setAccountType(userData?.account_type);
		}

		setDayGreet(getGreeting());
		getUserLogo();
	}, []);

	function getGreeting(): string {
		// Get the current hour of the day
		const today = new Date();
		const hour = today.getHours(); // Returns a number from 0 (midnight) to 23 (11 PM)
		// Determine the appropriate greeting based on the hour
		let greeting: string;
		if (hour >= 0 && hour < 5) {
			greeting = "Hi,";
		} else if (hour > 4 && hour < 12) {
			greeting = "Good morning!";
		} else if (hour < 18) {
			greeting = "Good day!";
		} else {
			greeting = "Good evening!";
		}

		return greeting;
	}

	const handleBackToPersonalAccount = async () => {
		const personal_token = localStorage.getItem('parent_token')
		const personal_data = JSON.parse(localStorage.getItem('parent_data') || "{}");
		const personal_id = localStorage.getItem('parent_id');
		login(Number(personal_id), personal_token || "", {...personal_data, is_customer_account: false});
		navigate('/manageCustomers');
	}

	const handlePricingClick = () => {
		if(accountType !== 0) return;
		setShowPricingModal(true);
	};

	const handleContactSales = () => {
		// Close pricing modal and open contact modal
		setShowPricingModal(false);
		setShowContactModal(true);
	};

	const userData = JSON.parse(localStorage.getItem("userData") || "{}");
	return (
		<>
		<header className="main-header">
			<div className="container-fluid">
				<nav className="navbar navbar-expand-lg">
					<div className="container-fluid d-flex justify-content-between">
						{/* <a className="" href="/"> */}
							<div className="card d-flex align-items-center" style={{
								minWidth: '8%',
								cursor: 'pointer'
							}} onClick={() => navigate('/')}>
								<LogoIcon />
							</div>
						{/* </a> */}
						
						<ul className="navbar-nav me-auto ms-md-5 d-flex justify-content-start">
							<li className="nav-item">
								<select
									name=""
									id=""
									className="form-select header-select"
									onChange={(e) => {
										selectUseCase(Number(e.target.value));
									}}
									value={useCase}
								>
									<option value="0" disabled>
										Areas of Interest
									</option>
									{useCases.map((useCase: UseCase, key) => (
										<option
											value={useCase.value}
											key={`use_case_options_${key}`}
											selected={useCase.value == Number(useCase)}
										>
											{useCase.name}
										</option>
									))}
								</select>
							</li>
						</ul>

						{userLogo !== "" && (
							<div className="d-flex justify-content-center align-items-center position-absolute" style={{
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								zIndex: 1000
							}}>
									<img
										src={userLogo}
										alt="User Logo"
										className="user-logo"
										style={{
											maxWidth: "100px",
											maxHeight: "50px",
											objectFit: "contain"
										}}
									/>
							</div>
						)}
						
						<div>
							{
								userData.is_customer_account && <button className="btn btn-sm btn-rounded-blue" style={{
									marginRight: '10%',
									cursor: 'pointer',
								}} onClick={handleBackToPersonalAccount}>Back to Service Provider Account</button>
							}
						</div>
						<div className="mx-2">
							<button 
								className="btn btn-sm btn-rounded" 
								style={{
									backgroundColor: '#5376F0',
									color: 'white',
									border: 'none',
									padding: '10px 25px',
									fontSize: '1rem',
									cursor: accountType !== 0 ? 'default' : 'pointer',
									boxShadow: '0 4px 15px rgba(0, 60, 158, 0.3)',
									transition: 'all 0.3s ease',
									marginRight: '10%'
								}} 
								onClick={handlePricingClick}
								onMouseEnter={(e) => {
									if(accountType !== 0) return;
									const target = e.target as HTMLButtonElement;
									target.style.backgroundColor = '#26397E !important';
									target.style.transform = 'translateY(-2px)';
								}}
								onMouseLeave={(e) => {
									if(accountType !== 0) return;
									const target = e.target as HTMLButtonElement;
									target.style.backgroundColor = '#5376F0 !important';
									target.style.transform = 'translateY(0)';
								}}
							>
								{accountType == 0 ? "Upgrade to PRO" : accountType== 1 ? "Enterprise Account" : accountType== 2 ? "Advisor Account" : accountType == 3 ? "Service Account" : accountType == 4 ? "Account Manager" : accountType == 5 ? "FMA Manager" : "Upgrade to PRO"}
							</button>
						</div>
						<div className="d-flex">
							<div className="dropdown">
								<a
									className="btn btn-light rounded"
									href="#"
									role="button"
									style={{
										borderRadius: '1.25rem',
										fontSize: '1.07rem',
										cursor: 'default',
										transition: 'all 0.3s ease',
									}}
								>
									<span className=" d-sm-inline-block">
										{dayGreet}
										{userData?.first_name && ` ${userData?.first_name} `}
									</span>
								</a>
							</div>
						</div>
						<div className="d-flex">
							<div className="dropdown">
								<div 
									className="btn btn-light rounded-circle dropdown card" 
									data-bs-toggle="dropdown"
									aria-expanded="false"
									role="button"
									style={{
										width: '40px',
										height: '40px',
										borderRadius: '50%',
										border: '1px solid white',
										alignItems: 'center',
										justifyContent: 'center',
										marginLeft: '10px',
									}}
								>
									<i className="bi bi-person" style={{
										fontSize: '1.8rem',
									}}></i>
									
								</div>
								<ul className="dropdown-menu">
											
											<li>
												<a className="dropdown-item" href="/changeAccountPassword">
													<i className="bi bi-key"></i> Change Password
												</a>
											</li>

											{/* {userData?.is_admin && (
												<li>
													<a className="dropdown-item" href="/adminFeatures">
														<i className="bi bi-robot"></i> Administration
													</a>
												</li>
											)}
											{(userData?.account_type === 2 || userData?.is_admin) && (
												<li>
													<a className="dropdown-item" href="/addData">
														<i className="bi bi-cloud-plus"></i> Add Data
													</a>
												</li>
											)}
											{userData?.is_admin && (
												<li>
													<a className="dropdown-item" href="/viewFormData">
														<i className="bi bi-cloud-plus"></i> View Data
													</a>
												</li>
											)} */}
											{/* {
												userData?.account_type === 3 && (
													<li>
														<a className="dropdown-item" href="/registerCustomers">
														<i className="bi bi-person-add"></i> Add Customers
														</a>
													</li>
												)
											}
											{userData?.account_type === 3 && (
												<li>
													<a className="dropdown-item" href="/manageCustomers">
														<i className="bi bi-cloud-plus"></i> Manage Customers
													</a>
												</li>
											)}
											<li>
												<a className="dropdown-item" href="/privacy-policy">
													<i className="bi bi-file-earmark-lock"></i> Privacy Policy
												</a>
											</li>
											<li>
												<a className="dropdown-item" href="/terms-and-conditions">
													<i className="bi bi-file-earmark-ruled"></i> Terms &
													Conditions
												</a>
											</li> */}
											<li>
												<a
													className="dropdown-item"
													href="#"
													onClick={handleLogout}
												>
													<i className="bi bi-lock"></i> Log out
												</a>
											</li>
								</ul>
							</div>
						</div>
					</div>
				</nav>
			</div>
		</header>

		{/* Use the new PricingModal component */}
		<PricingModal 
			showPricingModal={showPricingModal}
			setShowPricingModal={setShowPricingModal}
			handleContactSales={handleContactSales}
		/>

		{/* Contact Modal for sales */}
		<ContactModal 
			show={showContactModal} 
			onClose={() => setShowContactModal(false)} 
			type="sales" 
		/>
		</>
	);
};

export default ChatHeader;
